import { _ } from "libs";

import React, { useState } from "react";
import { Form, Field } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Profile, LocationSearchResult } from "../../types";
import CitySelect from "../forms/controls/citySelect";
import CountrySelect from "../forms/controls/countrySelect";
import PhoneNumberInput, { formatPhoneNumber, parsePhoneNumber } from "../forms/controls/phoneNumberInput";
import Modal from "../modal";
import PasswordChangeForm from "../passwordChangeForm";
import LanguageSelect from "../forms/controls/languageSelect";
import PhoneNumberVerify from "./phoneNumberVerify";
import { isNil } from "ramda";
import DeleteAccount from "../forms/controls/deleteAccount";

type Props = {
	onChange: Function;
	profile: Profile;
};

const hasPhoneNumberEntered = (profile: Profile) => {
	if (isNil(profile) || isNil(profile.mobilePhone)) {
		return false;
	}
	return profile.mobilePhone.length > 5;
};

const WhenFieldChanges = ({ field, becomes, set }) => (
	<Field name={set} subscription={{}}>
		{(
			// No subscription. We only use Field to get to the change function
			{ input: { onChange } }
		) => (
			<OnChange name={field}>
				{(city: LocationSearchResult) => {
					onChange((city.country as unknown) as React.ChangeEvent<string>);
				}}
			</OnChange>
		)}
	</Field>
);

type SubmitProps = {
	username: string;
	email: string;
	useNetwork: string;
	address1: string;
	city: any;
	zipCode: string;
	country: string;
	mobilePhone: string;
	language: string;
};

export function AccountForm({ onChange, profile }: Props) {
	const [showModal, setShowModal] = useState(false);
	const [phoneVerified, setPhoneVerified] = useState(profile.mobileValid);

	const openModal = () => {
		setShowModal(true);
	};

	const closeModal = () => {
		setShowModal(false);
	};

	return (
		<Form
			onSubmit={(
				{ username, email, useNetwork, address1, city, zipCode, country, mobilePhone, language }: SubmitProps,
				mutators
			) => {
				let cityValue = city;
				if (city && city.hasOwnProperty("value")) {
					cityValue = city.value;
				}
				onChange({
					username,
					email,
					useNetwork,
					address1,
					city: cityValue,
					zipCode,
					country: country.toUpperCase(),
					mobilePhone,
					language,
				});
			}}
			initialValues={{ ...profile }}
			render={({ handleSubmit, submitting, pristine, invalid, values }) => (
				<form onSubmit={handleSubmit} className="floatingButtons">
					<WhenFieldChanges field="city" becomes={values.city !== null} set="country" />

					<fieldset>
						<h5>{_("gotcourtsID.heading")}</h5>
						<Field name="username">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.username.label")}</label>
									<input {...input} type="text" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<Field name="email">
							{({ input, meta }) => (
								<div className="field">
									<label>{_("form.email.label")}</label>
									<input {...input} type="text" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
									{!profile.emailValid && (
										<p className="warning withIcon">{_("form.email.notVerified")}</p>
									)}
									{profile.emailValid ? (
										<p className="success withIcon">{_("form.email.verified")}</p>
									) : null}
								</div>
							)}
						</Field>
						<div className="field">
							<label>{_("form.password.label")}</label>
							<div className="passwordInput">
								<div className="value">••••••••</div>
								<span className="link blue" onClick={openModal}>
									{_("form.password.changeBtn")}
								</span>
							</div>
						</div>
						{showModal ? (
							<Modal
								title={_("changePassword.heading")}
								onCloseClicked={closeModal}
								className="scrollable"
							>
								<PasswordChangeForm callback={closeModal} />
							</Modal>
						) : null}
					</fieldset>
					<fieldset>
						<h5>{_("phoneNumber.heading")}</h5>
						<Field name="mobilePhone" format={formatPhoneNumber} parse={parsePhoneNumber}>
							{({ input, meta, ...rest }) => (
								<div className="field phoneNumber">
									<PhoneNumberInput {...{ input, meta, ...rest }} country={values.country} />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
									{phoneVerified ? (
										<p className="success withIcon">{_("form.phone.verified")}</p>
									) : null}
								</div>
							)}
						</Field>
						{hasPhoneNumberEntered(profile) && !phoneVerified ? (
							<div className="verifyPhoneNumber">
								<h6>{_("accountForm.verifyPhoneNumber.heading")}</h6>
								<PhoneNumberVerify onVerify={() => setPhoneVerified(true)} />
							</div>
						) : null}
						{hasPhoneNumberEntered(profile) || phoneVerified ? null : (
							<div className="note">{_("accountForm.phone.note")}</div>
						)}
					</fieldset>
					<fieldset>
						<h5>{_("playerNetwork.heading")}</h5>
						<Field name="useNetwork" type="checkbox">
							{({ input, meta }) => (
								<div className="field checkbox">
									<label>
										<input {...input} type="checkbox" />
										{_("accountForm.joinPlayerNetwork.label")}
									</label>
								</div>
							)}
						</Field>
						<div className="note">{_("onboarding.profileForm.playerNetwork.description")}</div>
					</fieldset>
					<fieldset>
						<h5>{_("address.heading")}</h5>
						<Field name="address1">
							{({ input, meta, ...rest }) => (
								<div className="field">
									<label>{_("form.street.label")}</label>
									<input {...input} type="text" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<Field name="zipCode">
							{({ input, meta, ...rest }) => (
								<div className="field">
									<label>{_("form.postalCode.label")}</label>
									<input {...input} type="text" />
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<Field name="city">
							{({ input, meta, ...rest }) => (
								<div className="field searchField">
									<label>{_("form.city.label")}</label>
									<CitySelect
										{...{ input, meta, ...rest }}
										getDisplayValue={(value) => {
											if (typeof value === "object") {
												return value.value;
											}
											return value;
										}}
									/>
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
						<Field name="country">
							{({ input, meta, ...rest }) => (
								<div className="field">
									<label>{_("form.country.label")}</label>
									<CountrySelect
										{...{ input, meta, ...rest }}
										prompt={_("accountForm.country.prompt")}
									/>
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
					</fieldset>
					<fieldset>
						<h5>{_("language.heading")}</h5>
						<Field name="language">
							{({ input, meta, ...rest }) => (
								<div className="field">
									{/* <label>{_("form.language.label")}</label> */}
									{<LanguageSelect {...{ input, meta, ...rest }} />}
									{meta.touched && meta.error && <p className="error">{meta.error}</p>}
								</div>
							)}
						</Field>
					</fieldset>
					{/**
						<fieldset>
							<DeleteAccount />
						</fieldset>
						**/
					}
					<div className="buttons">
						<button type="submit" className="button fullWidth" disabled={submitting || invalid}>
							{_("saveBtn")}
						</button>
					</div>
				</form>
			)}
		/>
	);
}

export default AccountForm;
